import { useEffect, useRef } from 'react';
import { useReducedMotion } from '@/lib/useReducedMotion';

type Point = [number, number, number];
const SEGMENTS = 144;

/** A continuous light ribbon, projected from a trefoil in three dimensions. */
const RIBBON = Array.from({ length: SEGMENTS + 1 }, (_, i) => {
  const t = i / SEGMENTS * Math.PI * 2;
  const radius = 1.75 + 0.62 * Math.cos(3 * t);
  const center: Point = [radius * Math.cos(2 * t), radius * Math.sin(2 * t), 0.78 * Math.sin(3 * t)];
  const twist = t * 1.5;
  const width: Point = [Math.cos(2 * t) * Math.cos(twist) * 0.24, Math.sin(2 * t) * Math.cos(twist) * 0.24, Math.sin(twist) * 0.24];
  return [
    center.map((v, j) => v + width[j]) as Point,
    center.map((v, j) => v - width[j]) as Point,
  ];
});

export const LightRibbonMark = ({ size }: { size: number }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    const mobile = size < 100;
    const dpr = Math.min(window.devicePixelRatio || 1, mobile ? 1.5 : 2);
    canvas.width = Math.round(size * dpr);
    canvas.height = Math.round(size * dpr);
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    let frame = 0;
    let visible = true;
    let elapsed = 0;
    let previous = 0;
    let lastPaint = 0;
    const interval = 1000 / (mobile ? 24 : 30);

    const paint = (time: number) => {
      const turn = 0.2 + time * 0.00052;
      const tilt = -0.52 + Math.sin(time * 0.00038) * 0.16;
      const roll = -0.12 + time * 0.00017;
      const project = ([x, y, z]: Point) => {
        const x1 = x * Math.cos(turn) + z * Math.sin(turn);
        const z1 = -x * Math.sin(turn) + z * Math.cos(turn);
        const y1 = y * Math.cos(tilt) - z1 * Math.sin(tilt);
        const z2 = y * Math.sin(tilt) + z1 * Math.cos(tilt);
        const perspective = 7 / (7 - z2);
        const x2 = x1 * Math.cos(roll) - y1 * Math.sin(roll);
        const y2 = x1 * Math.sin(roll) + y1 * Math.cos(roll);
        return { x: size / 2 + x2 * size * 0.165 * perspective, y: size / 2 + y2 * size * 0.165 * perspective, z: z2 };
      };
      const points = RIBBON.map(pair => pair.map(project));
      const faces = points.slice(0, -1).map((pair, i) => {
        const vertices = [pair[0], points[i + 1][0], points[i + 1][1], pair[1]];
        return { vertices, depth: vertices.reduce((sum, p) => sum + p.z, 0) / 4 };
      }).sort((a, b) => a.depth - b.depth);
      context.clearRect(0, 0, size, size);
      context.lineJoin = 'round';
      for (const { vertices, depth } of faces) {
        const light = Math.max(0, Math.min(1, (depth + 2.6) / 5.2));
        context.beginPath();
        vertices.forEach((p, i) => i === 0 ? context.moveTo(p.x, p.y) : context.lineTo(p.x, p.y));
        context.closePath();
        context.fillStyle = `rgb(${Math.round(35 + light * 130)} ${Math.round(84 + light * 150)} ${Math.round(119 + light * 124)})`;
        context.fill();
        // The illuminated edge follows the actual ribbon; no per-frame blur.
        context.beginPath();
        context.moveTo(vertices[0].x, vertices[0].y);
        context.lineTo(vertices[1].x, vertices[1].y);
        context.strokeStyle = `rgba(197,248,255,${0.28 + light * 0.67})`;
        context.lineWidth = mobile ? 0.7 : 1;
        context.stroke();
      }
      canvas.dataset.frames = String(Number(canvas.dataset.frames || 0) + 1);
    };

    const tick = (now: number) => {
      frame = 0;
      if (!visible || document.hidden || reduced) return;
      if (previous) elapsed += Math.min(now - previous, 100);
      previous = now;
      if (now - lastPaint >= interval) {
        paint(elapsed);
        lastPaint = now;
      }
      frame = requestAnimationFrame(tick);
    };
    const sync = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      previous = 0;
      if (visible && !document.hidden && !reduced) frame = requestAnimationFrame(tick);
    };
    const observer = typeof IntersectionObserver !== 'undefined' ? new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      sync();
    }) : null;
    observer?.observe(canvas);
    document.addEventListener('visibilitychange', sync);
    paint(0);
    sync();
    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer?.disconnect();
      document.removeEventListener('visibilitychange', sync);
    };
  }, [size, reduced]);

  return <canvas ref={canvasRef} data-light-ribbon aria-hidden="true" className="inline-block shrink-0 align-middle" style={{ width: size, height: size }} />;
};
